/* Sequoia Fitness | Website: https://www.sequoia.fitness | Author: Ali Hosseini | Copyright (c) 2026 sequoia.fitness. All rights reserved. */
/* Designed and developed by Pouria Shabani | https://linkedin.com/in/pouriashabani */

import { useEffect, useRef, useState } from 'react'

const APP = 'https://app.sequoia.fitness'
const BRIDGE_KEY = 'seq_br_v1'

function readCache() {
  try {
    const o = JSON.parse(sessionStorage.getItem(BRIDGE_KEY) || 'null')
    if (o && o.d && Date.now() - o.t < (o.d.logged_in === true ? 300000 : 30000)) return o.d
  } catch (e) {
    return null
  }
  return null
}

function writeCache(d) {
  try {
    sessionStorage.setItem(BRIDGE_KEY, JSON.stringify({ t: Date.now(), d }))
  } catch (e) {
    return
  }
}

function fetchBridge() {
  return fetch(APP + '/api/auth/bridge', { method: 'GET', credentials: 'include', cache: 'no-store', mode: 'cors' })
    .then((r) => (r.ok ? r.json() : null))
    .then((r) => {
      let d = r && r.data ? r.data : r
      d = d && d.logged_in === true ? d : { logged_in: false }
      writeCache(d)
      return d
    })
}

function logout() {
  try {
    sessionStorage.removeItem(BRIDGE_KEY)
  } catch (e) {
    return
  }
  fetch(APP + '/api/auth/logout', { method: 'POST', credentials: 'include', mode: 'cors' })
    .catch(() => {})
    .then(() => location.reload())
}

function Avatar({ user, onToggle, open }) {
  const fallback = 'a/' + (user.gender === 'female' ? 'female' : 'male') + '-placeholder.webp'
  const [src, setSrc] = useState(user.avatar_url ? APP + user.avatar_url : fallback)
  return (
    <button type="button" className="seq-avatar" aria-label="منوی حساب کاربری" aria-haspopup="menu" aria-expanded={open ? 'true' : 'false'} onClick={onToggle}>
      <img alt={user.first_name || ''} decoding="async" src={src} onError={() => setSrc(fallback)} />
    </button>
  )
}

export default function Account() {
  const [user, setUser] = useState(null)
  const [safari, setSafari] = useState(false)
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)

  useEffect(() => {
    if (!open) return undefined
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false)
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  useEffect(() => {
    setSafari(/^((?!chrome|chromium|android).)*safari/i.test(navigator.userAgent))
    const cached = readCache()
    if (cached) {
      if (cached.logged_in === true) setUser(cached)
      return undefined
    }
    let cancelled = false
    const run = () =>
      fetchBridge()
        .then((d) => {
          if (!cancelled && d.logged_in === true) setUser(d)
        })
        .catch(() => {})
    if ('requestIdleCallback' in window) {
      const id = requestIdleCallback(run, { timeout: 1500 })
      return () => {
        cancelled = true
        cancelIdleCallback(id)
      }
    }
    const id = setTimeout(run, 300)
    return () => {
      cancelled = true
      clearTimeout(id)
    }
  }, [])

  return (
    <div id="seq-acct" ref={rootRef} className={open ? 'is-open' : ''}>
      {user ? (
        <>
          <Avatar user={user} open={open} onToggle={() => setOpen((v) => !v)} />
          <div className="seq-acct-menu" role="menu">
            <div className="seq-acct-card">
              <div className="seq-acct-title">{user.first_name || 'حساب کاربری'}</div>
              <a className="seq-acct-item" href={APP + '/'} role="menuitem">پنل کاربری</a>
              <a className="seq-acct-item" href={APP + '/my-programs'} role="menuitem">برنامه‌های من</a>
              <button type="button" className="seq-acct-item seq-acct-out" role="menuitem" onClick={logout}>خروج</button>
            </div>
          </div>
        </>
      ) : (
        <>
          <a className="seq-plat" id="seq-acct-link" href={APP} title="پلتفرم هوشمند سکویا فیتنس" aria-label="پلتفرم هوشمند سکویا فیتنس">
            <video
              src={safari ? 'a/sequoia-panel-alpha.mp4' : 'a/sequoia-panel.webm'}
              poster="a/sequoia-panel-poster.webp"
              width="36"
              height="36"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              disablePictureInPicture
              aria-hidden="true"
            />
          </a>
        </>
      )}
    </div>
  )
}
